import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth.store';
import { useChannelsStore } from '@/store/channels.store';
import { useAgentTurnsStore } from '@/store/agent-turns.store';
import { useWorkspaceStore } from '@/store/workspace.store';
import type { Channel, ChannelStatus } from '@conversia/shared-types';

/**
 * Conecta el cliente Socket.io autenticado con el access token vigente.
 *
 * El backend de WS (`apps/api/src/ws`) expone el namespace en `path: '/ws'` con
 * auth JWT en el handshake. Este hook se monta una vez (Layout) y reenvía los
 * eventos del bus realtime a react-query / al store de canales.
 * Reenvía `channel.status` al store de canales (QR de WhatsApp incluido).
 */

const SOCKET_URL = (import.meta.env.VITE_WS_URL as string | undefined) ?? '';

export function useSocket(): Socket | null {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setChannelStatus = useChannelsStore((s) => s.setStatus);
  const activeId = useWorkspaceStore((s) => s.activeId);
  const qc = useQueryClient();
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!accessToken) return;

    const socket = io(SOCKET_URL || undefined, {
      path: '/ws',
      transports: ['websocket'],
      auth: { token: accessToken },
      autoConnect: true,
      reconnection: true,
    });
    socketRef.current = socket;

    // Al conectar, el cliente notifica el negocio activo. El servidor valida
    // la suscripción y solo envía eventos en vivo para ESE workspace.
    socket.on('connect', () => {
      const actual = useWorkspaceStore.getState().activeId;
      if (actual) socket.emit('workspace:subscribe', actual);
    });

    socket.on(
      'channel.status',
      (payload: { channelId: string; status: ChannelStatus; qr?: string; error?: string }) => {
        setChannelStatus(payload.channelId, payload.status, payload.qr, payload.error);
        // El badge de la lista de canales se renderiza desde react-query, no desde
        // el store, así que parcheamos la cache para que el estado cambie en vivo
        // (amarillo→verde al conectar, verde→rojo al caerse) sin refrescar la
        // página. Optimista: evita el round-trip de un invalidate+refetch.
        qc.setQueryData<Channel[]>(['channels', 'list'], (prev) =>
          prev?.map((c) =>
            c.id === payload.channelId
              ? {
                  ...c,
                  status: payload.status,
                  lastError: payload.status === 'CONNECTED' ? null : payload.error ?? c.lastError,
                }
              : c,
          ),
        );
      },
    );

    // Refresca la lista y los mensajes de la conversación afectada en vivo.
    const invalidateConversation = (payload: { conversationId: string }) => {
      void qc.invalidateQueries({ queryKey: ['conversation', payload.conversationId, 'messages'] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    };
    socket.on('message.created', invalidateConversation);
    socket.on('message.status', invalidateConversation);
    socket.on('conversation.updated', invalidateConversation);

    // Turno del agente en vuelo → indicador "Respondiendo…" (estado real).
    socket.on('agent.turn', (payload: { conversationId: string; phase: 'started' | 'finished' }) => {
      useAgentTurnsStore.getState().setTurn(payload.conversationId, payload.phase);
    });

    // El número de un contacto puede resolverse en background (JID→teléfono):
    // refresca su ficha y las listas que muestran su nombre/número.
    socket.on('contact.updated', (payload: { contactId: string }) => {
      void qc.invalidateQueries({ queryKey: ['contact', payload.contactId] });
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [accessToken, setChannelStatus, qc]);

  // Cuando el negocio activo cambia, notifica al servidor para filtrar eventos en vivo.
  useEffect(() => {
    if (activeId && socketRef.current?.connected) socketRef.current.emit('workspace:subscribe', activeId);
  }, [activeId]);

  return socketRef.current;
}
